import React, { useState, useRef, useEffect, useCallback } from 'react';
import { requestOrderOTP, verifyOrderOTP } from '../services/firebase';

interface OrderOTPModalProps {
  open: boolean;
  phone: string;
  amount: number;
  onVerified: (verificationToken: string) => void;
  onClose: () => void;
  lang: 'de' | 'en';
}

const OrderOTPModal: React.FC<OrderOTPModalProps> = ({ open, phone, amount, onVerified, onClose, lang }) => {
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [hasSent, setHasSent] = useState(false);
  const [channel, setChannel] = useState<'sms' | 'whatsapp'>('sms');
  const [channelUsed, setChannelUsed] = useState<'sms' | 'whatsapp'>('sms');
  // Two-phase flow:
  //   phase 'confirm' — show order summary, wait for user to click "Confirm & send code"
  //   phase 'verify'  — code sent, show OTP input. Entering code auto-places order.
  const [phase, setPhase] = useState<'confirm' | 'verify'>('confirm');

  const otpRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Send initial OTP when modal opens
  const sendCode = useCallback(async () => {
    setSending(true);
    setError('');
    try {
      const result: any = await requestOrderOTP({ phone, amount, channel });
      const used = result?.data?.channel_used;
      if (used === 'sms' || used === 'whatsapp') setChannelUsed(used);
      setCountdown(60);
      setHasSent(true);
      setTimeout(() => otpRefs[0].current?.focus(), 150);
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('Too many')) {
        setError(lang === 'de' ? 'Zu viele Versuche. Bitte später erneut versuchen.' : 'Too many attempts. Try again later.');
      } else {
        setError(lang === 'de' ? 'SMS konnte nicht gesendet werden.' : 'Failed to send SMS.');
      }
    } finally {
      setSending(false);
    }
  // eslint-disable-next-line
  }, [phone, amount, lang, channel]);

  useEffect(() => {
    if (!open) {
      // Reset on close
      setOtp(['', '', '', '']);
      setError('');
      setHasSent(false);
      setPhase('confirm');
    }
  }, [open]);

  const handleConfirmAndSend = async () => {
    setPhase('verify');
    await sendCode();
  };

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (i: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[i] = value;
    setOtp(newOtp);
    if (value && i < 3) otpRefs[i + 1].current?.focus();
    if (value && i === 3 && newOtp.every(d => d !== '')) verify(newOtp.join(''));
  };

  const handleKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      otpRefs[3].current?.focus();
      verify(pasted);
    }
  };

  const verify = async (code: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await verifyOrderOTP({ phone, code });
      const data = result.data as any;
      if (data?.success && data?.verification_token) {
        onVerified(data.verification_token);
      } else {
        setError(lang === 'de' ? 'Verifizierung fehlgeschlagen' : 'Verification failed');
      }
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('expired')) setError(lang === 'de' ? 'Code abgelaufen. Neuen Code anfordern.' : 'Code expired. Request a new one.');
      else if (msg.includes('Invalid')) setError(lang === 'de' ? 'Ungültiger Code.' : 'Invalid code.');
      else if (msg.includes('Too many')) setError(lang === 'de' ? 'Zu viele Versuche.' : 'Too many attempts.');
      else setError(lang === 'de' ? 'Verifizierung fehlgeschlagen' : 'Verification failed');
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (countdown <= 0) sendCode();
  };

  if (!open) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#1a1d2e', borderRadius: 20, padding: 28,
        maxWidth: 380, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        border: '1px solid #2a2d42',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 6 }}>
            {phase === 'confirm' ? '🧾' : '📱'}
          </div>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
            {phase === 'confirm'
              ? (lang === 'de' ? 'Bestellung bestätigen' : 'Confirm Your Order')
              : (lang === 'de' ? 'Code eingeben' : 'Enter Code')}
          </h2>
          {phase === 'confirm' ? (
            <p style={{ color: '#9ca3af', margin: '10px 0 0', fontSize: '0.85rem', lineHeight: 1.5 }}>
              {lang === 'de'
                ? 'Nach Bestätigung senden wir einen 4-stelligen Code an:'
                : 'After confirming we will send a 4-digit code to:'}
            </p>
          ) : (
            <p style={{ color: '#9ca3af', margin: '8px 0 0', fontSize: '0.85rem', lineHeight: 1.4 }}>
              {sending
                ? (lang === 'de' ? `${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} wird gesendet...` : `Sending ${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}...`)
                : (lang === 'de'
                  ? `Wir haben Ihnen einen 4-stelligen Code per ${channelUsed === 'whatsapp' ? '🟢 WhatsApp' : '💬 SMS'} gesendet an:`
                  : `We sent a 4-digit code via ${channelUsed === 'whatsapp' ? '🟢 WhatsApp' : '💬 SMS'} to:`)}
            </p>
          )}
          <p style={{ color: '#94e815', fontWeight: 700, margin: '6px 0', fontSize: '0.95rem' }}>
            {phone}
          </p>
          {amount > 0 && (
            <p style={{ color: '#666', fontSize: '0.75rem', margin: '4px 0 0' }}>
              {lang === 'de' ? 'Bestellbetrag' : 'Order amount'}: <span style={{ color: '#94e815', fontWeight: 600 }}>€{amount.toFixed(2)}</span>
            </p>
          )}
        </div>

        {phase === 'confirm' ? (
          <>
            <p style={{ color: '#9ca3af', fontSize: '0.78rem', textAlign: 'center', marginBottom: 14, lineHeight: 1.5 }}>
              {lang === 'de'
                ? 'Dies ist der letzte Schritt vor der Codeeingabe. Nach Eingabe des Codes wird Ihre Bestellung sofort aufgegeben.'
                : 'This is the last step before entering the code. Once you enter the code, your order will be placed immediately.'}
            </p>

            {error && (
              <p style={{ color: '#ef5350', fontSize: '0.8rem', textAlign: 'center', marginBottom: 10 }}>
                {error}
              </p>
            )}

            <button
              onClick={handleConfirmAndSend}
              disabled={sending}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 10,
                border: 'none', background: sending ? '#555' : '#94e815', color: '#1a1d2e',
                fontSize: '1rem', fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
                marginTop: 4,
              }}>
              {sending
                ? (lang === 'de' ? 'Code wird gesendet...' : 'Sending code...')
                : (lang === 'de' ? 'Bestätigen & Code senden' : 'Confirm & Send Code')}
            </button>

            <button onClick={onClose} disabled={sending} style={{
              width: '100%', background: 'transparent', border: 'none', color: '#9ca3af',
              fontSize: '0.85rem', cursor: sending ? 'not-allowed' : 'pointer',
              padding: '10px 0', marginTop: 8,
            }}>
              {lang === 'de' ? 'Abbrechen' : 'Cancel'}
            </button>
          </>
        ) : (
          <>
            {/* OTP input boxes */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKey(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  disabled={loading || sending}
                  style={{
                    width: 56, height: 64, textAlign: 'center', fontSize: '1.8rem',
                    fontWeight: 700, borderRadius: 12, border: `2px solid ${digit ? '#94e815' : '#3a3d52'}`,
                    background: '#1e2130', color: '#fff', outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#94e815'; }}
                  onBlur={(e) => { if (!digit) e.target.style.borderColor = '#3a3d52'; }}
                />
              ))}
            </div>

            {error && (
              <p style={{ color: '#ef5350', fontSize: '0.8rem', textAlign: 'center', marginTop: 4, marginBottom: 8 }}>
                {error}
              </p>
            )}

            {loading && (
              <p style={{ color: '#94e815', fontSize: '0.85rem', textAlign: 'center', marginBottom: 8 }}>
                {lang === 'de' ? 'Bestellung wird aufgegeben...' : 'Placing your order...'}
              </p>
            )}

            {/* Channel swap: try other channel if code didn't arrive */}
            {hasSent && !loading && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button
                  onClick={() => {
                    const newCh: 'sms' | 'whatsapp' = channelUsed === 'whatsapp' ? 'sms' : 'whatsapp';
                    setChannel(newCh);
                    setTimeout(() => sendCode(), 50);
                  }}
                  disabled={sending}
                  style={{
                    background: 'transparent', border: '1px solid #3a3d52',
                    color: '#9ca3af', fontSize: '0.75rem', padding: '6px 12px',
                    borderRadius: 8, cursor: sending ? 'default' : 'pointer',
                  }}>
                  {channelUsed === 'whatsapp'
                    ? (lang === 'de' ? '💬 Stattdessen per SMS senden' : '💬 Send via SMS instead')
                    : (lang === 'de' ? '🟢 Stattdessen per WhatsApp senden' : '🟢 Send via WhatsApp instead')}
                </button>
              </div>
            )}

            {/* Resend + Cancel */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8 }}>
              <button onClick={onClose} disabled={loading} style={{
                background: 'transparent', border: 'none', color: '#9ca3af',
                fontSize: '0.85rem', cursor: loading ? 'not-allowed' : 'pointer', padding: '8px 12px',
              }}>
                {lang === 'de' ? 'Abbrechen' : 'Cancel'}
              </button>
              <button onClick={handleResend} disabled={countdown > 0 || sending} style={{
                background: 'transparent', border: 'none',
                color: countdown > 0 || sending ? '#666' : '#94e815',
                fontSize: '0.85rem', cursor: countdown > 0 || sending ? 'default' : 'pointer', padding: '8px 12px',
                fontWeight: 600,
              }}>
                {sending
                  ? (lang === 'de' ? 'Wird gesendet...' : 'Sending...')
                  : countdown > 0
                    ? (lang === 'de' ? `Erneut in ${countdown}s` : `Resend in ${countdown}s`)
                    : (lang === 'de' ? 'Code erneut senden' : 'Resend code')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderOTPModal;
